import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Save, Plus, Trash2, ChevronUp, ChevronDown, Upload, Loader2, ExternalLink, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import SectionRenderer from "../../components/SectionRenderer";
import Seo from "../../components/Seo";

const inp = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm";

const DEFS = {
  hero: { label: "Hero", def: { type: "hero", heading: "Your headline here", subheading: "A short supporting line.", image: "", cta_text: "Get Started", cta_link: "/properties" } },
  features: { label: "Features", def: { type: "features", heading: "Why choose us", subheading: "", items: [{ title: "Feature one", description: "Describe it." }] } },
  stats: { label: "Stats", def: { type: "stats", items: [{ value: "500+", label: "Verified Properties" }] } },
  properties: { label: "Property List", def: { type: "properties", heading: "Featured Properties", mode: "featured", limit: 4 } },
  gallery: { label: "Image Gallery", def: { type: "gallery", heading: "Gallery", images: [] } },
  testimonials: { label: "Testimonials", def: { type: "testimonials", heading: "What clients say", items: [{ name: "Client Name", role: "Buyer", quote: "Great experience." }] } },
  faq: { label: "FAQ", def: { type: "faq", heading: "FAQs", items: [{ question: "Question?", answer: "Answer." }] } },
  text: { label: "Text / Rich Content", def: { type: "text", heading: "", body_html: "<p>Write content here…</p>" } },
  cta: { label: "Call To Action", def: { type: "cta", heading: "Ready to find your property?", subheading: "", button_text: "Talk to Expert", button_link: "/contact" } },
  image: { label: "Single Image", def: { type: "image", image: "", caption: "" } },
  contact: { label: "Contact Form", def: { type: "contact", heading: "Get in touch", subheading: "Leave your details and we'll call you." } },
};

function UploadBtn({ onDone }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onDone(data.url); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (<><input ref={ref} type="file" accept="image/*" onChange={up} className="hidden" /><Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}</Button></>);
}

function ImageField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <div className="flex gap-2 mt-1">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" className={inp} />
        <UploadBtn onDone={onChange} />
      </div>
      {value && <img src={fileUrl(value)} alt="" className="w-full h-24 object-cover rounded-lg mt-2 bg-slate-100" />}
    </div>
  );
}

function ListEditor({ items, onChange, fields, addLabel }) {
  const set = (i, k, v) => { const c = [...items]; c[i] = { ...c[i], [k]: v }; onChange(c); };
  const add = () => onChange([...(items || []), fields.reduce((a, f) => ({ ...a, [f.k]: "" }), {})]);
  const del = (i) => onChange(items.filter((_, ix) => ix !== i));
  return (
    <div className="space-y-3">
      {(items || []).map((it, i) => (
        <div key={i} className="border border-slate-100 rounded-xl p-3 space-y-2 bg-slate-50/50">
          <div className="flex justify-between items-center"><span className="text-xs text-slate-400">Item {i + 1}</span><button onClick={() => del(i)} className="text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button></div>
          {fields.map((f) => f.area
            ? <textarea key={f.k} value={it[f.k] || ""} onChange={(e) => set(i, f.k, e.target.value)} placeholder={f.label} rows={2} className={inp} />
            : <input key={f.k} value={it[f.k] || ""} onChange={(e) => set(i, f.k, e.target.value)} placeholder={f.label} className={inp} />)}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add}><Plus className="w-4 h-4 mr-1" /> {addLabel || "Add item"}</Button>
    </div>
  );
}

function SectionEditor({ s, update }) {
  const set = (k, v) => update({ ...s, [k]: v });
  switch (s.type) {
    case "hero":
      return (<div className="space-y-3">
        <input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Heading" className={inp} />
        <textarea value={s.subheading} onChange={(e) => set("subheading", e.target.value)} placeholder="Subheading" rows={2} className={inp} />
        <ImageField label="Background image (optional)" value={s.image} onChange={(v) => set("image", v)} />
        <div className="grid grid-cols-2 gap-2"><input value={s.cta_text} onChange={(e) => set("cta_text", e.target.value)} placeholder="Button text" className={inp} /><input value={s.cta_link} onChange={(e) => set("cta_link", e.target.value)} placeholder="Button link e.g. /properties" className={inp} /></div>
      </div>);
    case "features":
      return (<div className="space-y-3">
        <input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Section heading" className={inp} />
        <input value={s.subheading} onChange={(e) => set("subheading", e.target.value)} placeholder="Subheading (optional)" className={inp} />
        <ListEditor items={s.items} onChange={(v) => set("items", v)} fields={[{ k: "title", label: "Title" }, { k: "description", label: "Description", area: true }]} addLabel="Add feature" />
      </div>);
    case "stats":
      return <ListEditor items={s.items} onChange={(v) => set("items", v)} fields={[{ k: "value", label: "Value e.g. 500+" }, { k: "label", label: "Label" }]} addLabel="Add stat" />;
    case "properties":
      return (<div className="space-y-3">
        <input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Heading" className={inp} />
        <div className="grid grid-cols-2 gap-2">
          <Select value={s.mode} onValueChange={(v) => set("mode", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
            <SelectItem value="all">All</SelectItem><SelectItem value="featured">Featured</SelectItem><SelectItem value="urgent_sale">Urgent Sale</SelectItem><SelectItem value="price_reduced">Price Reduced</SelectItem><SelectItem value="new_listing">New Listings</SelectItem>
          </SelectContent></Select>
          <input type="number" value={s.limit} onChange={(e) => set("limit", Number(e.target.value))} placeholder="Count" className={inp} />
        </div>
      </div>);
    case "gallery":
      return (<div className="space-y-3">
        <input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Heading" className={inp} />
        <div className="grid grid-cols-3 gap-2">{(s.images || []).map((img, i) => (
          <div key={i} className="relative"><img src={fileUrl(img)} alt="" className="w-full h-20 object-cover rounded-lg" /><button onClick={() => set("images", s.images.filter((_, ix) => ix !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5"><Trash2 className="w-3 h-3 text-rose-500" /></button></div>
        ))}</div>
        <UploadBtn onDone={(url) => set("images", [...(s.images || []), url])} />
      </div>);
    case "testimonials":
      return (<div className="space-y-3">
        <input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Heading" className={inp} />
        <ListEditor items={s.items} onChange={(v) => set("items", v)} fields={[{ k: "quote", label: "Quote", area: true }, { k: "name", label: "Name" }, { k: "role", label: "Role" }]} addLabel="Add testimonial" />
      </div>);
    case "faq":
      return (<div className="space-y-3">
        <input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Heading" className={inp} />
        <ListEditor items={s.items} onChange={(v) => set("items", v)} fields={[{ k: "question", label: "Question" }, { k: "answer", label: "Answer", area: true }]} addLabel="Add FAQ" />
      </div>);
    case "text":
      return (<div className="space-y-3">
        <input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Heading (optional)" className={inp} />
        <textarea value={s.body_html} onChange={(e) => set("body_html", e.target.value)} placeholder="HTML content" rows={6} className={`${inp} font-mono text-xs`} />
      </div>);
    case "cta":
      return (<div className="space-y-3">
        <input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Heading" className={inp} />
        <input value={s.subheading} onChange={(e) => set("subheading", e.target.value)} placeholder="Subheading" className={inp} />
        <div className="grid grid-cols-2 gap-2"><input value={s.button_text} onChange={(e) => set("button_text", e.target.value)} placeholder="Button text" className={inp} /><input value={s.button_link} onChange={(e) => set("button_link", e.target.value)} placeholder="Button link" className={inp} /></div>
      </div>);
    case "image":
      return (<div className="space-y-3"><ImageField label="Image" value={s.image} onChange={(v) => set("image", v)} /><input value={s.caption} onChange={(e) => set("caption", e.target.value)} placeholder="Caption (optional)" className={inp} /></div>);
    case "contact":
      return (<div className="space-y-3"><input value={s.heading} onChange={(e) => set("heading", e.target.value)} placeholder="Heading" className={inp} /><input value={s.subheading} onChange={(e) => set("subheading", e.target.value)} placeholder="Subheading" className={inp} /></div>);
    default: return null;
  }
}

export default function PageBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState("hero");
  const { data } = useQuery({ queryKey: ["page-edit", id], queryFn: async () => (await api.get(`/admin/pages/${id}`)).data });
  useEffect(() => { if (data) setP(data); }, [data]);

  if (!p) return <div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setSection = (i, v) => { const c = [...p.sections]; c[i] = v; set("sections", c); };
  const addSection = () => set("sections", [...(p.sections || []), JSON.parse(JSON.stringify(DEFS[addType].def))]);
  const moveSection = (i, dir) => { const c = [...p.sections]; const j = i + dir; if (j < 0 || j >= c.length) return; [c[i], c[j]] = [c[j], c[i]]; set("sections", c); };
  const delSection = (i) => set("sections", p.sections.filter((_, ix) => ix !== i));

  const save = async (status) => {
    setSaving(true);
    try {
      const body = { title: p.title, slug: p.slug, menu_label: p.menu_label, show_in_menu: p.show_in_menu, status: status || p.status, sections: p.sections, seo_title: p.seo_title, seo_description: p.seo_description };
      const { data } = await api.put(`/admin/pages/${id}`, body); setP(data);
      toast.success(status === "published" ? "Published!" : "Saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl">
      <Seo title={`Build: ${p.title} | Admin`} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl font-extrabold text-slate-900">Page Builder</h1><p className="text-slate-500 text-sm font-mono">/p/{p.slug}</p></div>
        <div className="flex gap-2">
          {p.status === "published" && <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><ExternalLink className="w-4 h-4 mr-1" /> View</Button></a>}
          <Button variant="outline" size="sm" onClick={() => save()} disabled={saving} data-testid="page-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save</Button>
          <Button size="sm" onClick={() => save("published")} disabled={saving} className="bg-indigo-900 hover:bg-indigo-800" data-testid="page-publish"><Eye className="w-4 h-4 mr-1" /> Publish</Button>
        </div>
      </div>

      {/* Page settings */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-5 grid sm:grid-cols-2 gap-4">
        <div><label className="text-xs text-slate-500">Page Title</label><input value={p.title} onChange={(e) => set("title", e.target.value)} className={`${inp} mt-1`} data-testid="pb-title" /></div>
        <div><label className="text-xs text-slate-500">URL Slug</label><input value={p.slug} onChange={(e) => set("slug", e.target.value)} className={`${inp} mt-1`} /></div>
        <div><label className="text-xs text-slate-500">Menu Label</label><input value={p.menu_label || ""} onChange={(e) => set("menu_label", e.target.value)} className={`${inp} mt-1`} /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700 mt-6"><Switch checked={!!p.show_in_menu} onCheckedChange={(v) => set("show_in_menu", v)} data-testid="pb-show-menu" /> Show in main menu</label>
        <div><label className="text-xs text-slate-500">SEO Title</label><input value={p.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} className={`${inp} mt-1`} /></div>
        <div><label className="text-xs text-slate-500">SEO Description</label><input value={p.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} className={`${inp} mt-1`} /></div>
      </div>

      {/* Sections */}
      <div className="space-y-4 mt-6">
        {(p.sections || []).map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`section-${i}`}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading font-bold text-slate-900 flex items-center gap-2"><span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">{DEFS[s.type]?.label || s.type}</span></span>
              <div className="flex gap-1">
                <button onClick={() => moveSection(i, -1)} className="p-1.5 hover:bg-slate-100 rounded-lg"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => moveSection(i, 1)} className="p-1.5 hover:bg-slate-100 rounded-lg"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => delSection(i)} className="p-1.5 hover:bg-rose-50 rounded-lg"><Trash2 className="w-4 h-4 text-rose-500" /></button>
              </div>
            </div>
            <SectionEditor s={s} update={(v) => setSection(i, v)} />
          </div>
        ))}
      </div>

      {/* Add section */}
      <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-5 mt-4 flex items-center gap-3">
        <Select value={addType} onValueChange={setAddType}>
          <SelectTrigger className="w-56" data-testid="pb-add-type"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(DEFS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={addSection} className="bg-indigo-900 hover:bg-indigo-800" data-testid="pb-add-section"><Plus className="w-4 h-4 mr-1" /> Add Section</Button>
      </div>

      {/* Live preview */}
      {(p.sections || []).length > 0 && (
        <div className="mt-8">
          <h3 className="font-heading font-bold text-slate-900 mb-2">Live Preview</h3>
          <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
            {p.sections.map((s, i) => <SectionRenderer key={i} section={s} />)}
          </div>
        </div>
      )}
    </div>
  );
}
